import * as THREE from 'three';
import type { BuildAnchorDef, BuildPieceDef } from './catalog';
import type { PlacedPiece, ResolvedAnchor } from './types';

// Anchor resolution: turn a piece's local anchor defs into world-space ResolvedAnchors (for hosts)
// or cached local transforms (for guests), and test guest<->host family compatibility. A placed
// piece's host anchors are resolved against its world matrix; the result is cached per instance and
// invalidated when the instance moves (placed pieces are static, so this is effectively one-time).

const _m = new THREE.Matrix4();
const _q = new THREE.Quaternion();
const _p = new THREE.Vector3();
const _n = new THREE.Vector3();
const UP = new THREE.Vector3(0, 1, 0);

function defQuat(def: BuildAnchorDef): THREE.Quaternion {
  return _q.set(def.localQuaternion[0], def.localQuaternion[1], def.localQuaternion[2], def.localQuaternion[3]);
}

// World matrix of a placed piece from its stored position + quaternion.
export function pieceMatrix(p: PlacedPiece, out = new THREE.Matrix4()): THREE.Matrix4 {
  return out.compose(p.position, p.quaternion, _p.set(1, 1, 1));
}

// Resolve one host anchor of a placed piece into world space. The anchor normal is the local +Y of
// its quaternion frame for top anchors, or the quaternion's forward (outward) for edge anchors; we
// derive it generically by rotating local +Y, then for edge families re-derive outward from the
// quaternion's yaw. To stay simple and correct, we transform a unit normal carried in the def's
// orientation: top anchors point +Y, edge/outward anchors point along the quaternion's forward.
export function resolveHostAnchor(p: PlacedPiece, def: BuildAnchorDef, worldMatrix: THREE.Matrix4): ResolvedAnchor {
  const pos = new THREE.Vector3(def.localPosition[0], def.localPosition[1], def.localPosition[2]).applyMatrix4(worldMatrix);
  // Orientation: extract the piece world quaternion and combine with the anchor's local quaternion.
  worldMatrix.decompose(_p, _q, _n);
  const aq = new THREE.Quaternion().copy(_q).multiply(defQuat(def));
  // Normal: top/cap anchors carry +Y; edge anchors carry their outward direction (the quat forward
  // mapped from world +Z). We pick +Y when the local position has no XZ offset (a cap), else forward.
  const isCap = def.localPosition[0] === 0 && def.localPosition[2] === 0 && def.family.startsWith('wall_top');
  const localNormal = isCap || def.family === 'ground' || def.family === 'floor_top'
    ? UP.clone()
    : new THREE.Vector3(0, 0, 1).applyQuaternion(aq).setY(0).normalize();
  return {
    pieceId: p.id,
    id: def.id,
    family: def.family,
    accepts: def.accepts,
    worldMatrix: worldMatrix.clone(),
    worldPosition: pos,
    worldNormal: localNormal.lengthSq() < 1e-9 ? UP.clone() : localNormal,
    maxSnapDistance: def.maxSnapDistance,
    maxAngleDegrees: def.maxAngleDegrees,
  };
}

// All host anchors of a placed piece in world space (the resolver the spatial index calls).
export function hostAnchorsOf(p: PlacedPiece, def: BuildPieceDef): ResolvedAnchor[] {
  const wm = pieceMatrix(p, _m).clone();
  const out: ResolvedAnchor[] = [];
  for (const a of def.anchors) {
    if (a.role !== 'host') continue;
    out.push(resolveHostAnchor(p, a, wm));
  }
  return out;
}

